import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { epicsApi } from '@/utils/api';
import type { Epic, EpicAllocation, ApiResponse } from '@/types';

interface EpicsState {
  epics: Epic[];
  currentEpic: Epic | null;
  priorityRankings: any[];
  allocations: EpicAllocation[];
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isPrioritizing: boolean;
  isManagingAllocations: boolean;
  error: string | null;
  allocationError: string | null;
  filters: {
    investibleStructureId?: string;
    status?: string;
    programId?: string;
    supplyingProgramId?: string;  // Filter EPICs where this program supplies capacity
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  sortBy: 'priority' | 'name' | 'status' | 'createdAt';
  sortOrder: 'asc' | 'desc';
}

const initialState: EpicsState = {
  epics: [],
  currentEpic: null,
  priorityRankings: [],
  allocations: [],
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isPrioritizing: false,
  isManagingAllocations: false,
  error: null,
  allocationError: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  sortBy: 'priority',
  sortOrder: 'asc',
};

// Async thunks
export const fetchEpics = createAsyncThunk(
  'epics/fetchEpics',
  async (params?: {
    investibleStructureId?: string;
    status?: string;
    programId?: string;
    supplyingProgramId?: string;  // Filter EPICs where this program supplies capacity
    page?: number;
    limit?: number;
  }, { rejectWithValue }) => {
    try {
      const response = await epicsApi.getAll(params);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch EPICs');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch EPICs');
    }
  }
);

export const fetchEpicById = createAsyncThunk(
  'epics/fetchEpicById',
  async (id: string, { rejectWithValue }) => {
    const response = await epicsApi.getById(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch EPIC');
    }
    return (response.data as any)?.epic ?? response.data;
  }
);

export const createEpic = createAsyncThunk(
  'epics/createEpic',
  async (epicData: Partial<Epic>, { rejectWithValue }) => {
    const response = await epicsApi.create(epicData);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to create EPIC');
    }
    return (response.data as any)?.epic ?? response.data;
  }
);

export const updateEpic = createAsyncThunk(
  'epics/updateEpic',
  async ({ id, data }: { id: string; data: Partial<Epic> }, { rejectWithValue }) => {
    const response = await epicsApi.update(id, data);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to update EPIC');
    }
    return (response.data as any)?.epic ?? response.data;
  }
);

export const deleteEpic = createAsyncThunk(
  'epics/deleteEpic',
  async (id: string, { rejectWithValue }) => {
    const response = await epicsApi.delete(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to delete EPIC');
    }
    return id;
  }
);

export const updateEpicStatus = createAsyncThunk(
  'epics/updateEpicStatus',
  async ({ id, status }: { id: string; status: string }, { rejectWithValue }) => {
    const response = await epicsApi.updateStatus(id, status);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to update EPIC status');
    }
    return (response.data as any)?.epic ?? response.data;
  }
);

export const updateEpicPriority = createAsyncThunk(
  'epics/updateEpicPriority',
  async ({ id, priority }: { id: string; priority: number }, { rejectWithValue }) => {
    const response = await epicsApi.updatePriority(id, priority);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to update EPIC priority');
    }
    return (response.data as any)?.epic ?? response.data;
  }
);

export const fetchPriorityRankings = createAsyncThunk(
  'epics/fetchPriorityRankings',
  async ({ isId, includeScores }: { isId: string; includeScores?: boolean }, { rejectWithValue }) => {
    const response = await epicsApi.getPriorityRankings(isId, includeScores);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch priority rankings');
    }
    return response.data;
  }
);

export const autoAssignPriorities = createAsyncThunk(
  'epics/autoAssignPriorities',
  async ({ isId, factors }: { isId: string; factors?: any }, { rejectWithValue }) => {
    const response = await epicsApi.autoAssignPriorities(isId, factors);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to auto-assign priorities');
    }
    return response.data;
  }
);

export const reorderPriorities = createAsyncThunk(
  'epics/reorderPriorities',
  async ({ isId, epicIds }: { isId: string; epicIds: string[] }, { rejectWithValue }) => {
    const response = await epicsApi.reorderPriorities(isId, epicIds);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to reorder priorities');
    }
    return response.data;
  }
);

export const bulkUpdatePriorities = createAsyncThunk(
  'epics/bulkUpdatePriorities',
  async (updates: Array<{ epicId: string; newPriority: number }>, { rejectWithValue }) => {
    const response = await epicsApi.bulkUpdatePriorities(updates);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to bulk update priorities');
    }
    return response.data;
  }
);

// Allocation management thunks
export const fetchEpicAllocations = createAsyncThunk(
  'epics/fetchEpicAllocations',
  async (epicId: string, { rejectWithValue }) => {
    try {
      const response = await epicsApi.getAllocations(epicId);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch allocations');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch allocations');
    }
  }
);

export const createEpicAllocation = createAsyncThunk(
  'epics/createEpicAllocation',
  async ({ epicId, allocation }: { 
    epicId: string; 
    allocation: Omit<EpicAllocation, 'id' | 'createdAt' | 'updatedAt'> 
  }, { rejectWithValue }) => {
    try {
      const response = await epicsApi.createAllocation(epicId, allocation);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to create allocation');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to create allocation');
    }
  }
);

export const updateEpicAllocation = createAsyncThunk(
  'epics/updateEpicAllocation',
  async ({ allocationId, data }: { 
    allocationId: string; 
    data: Partial<EpicAllocation> 
  }, { rejectWithValue }) => {
    try {
      const response = await epicsApi.updateAllocation(allocationId, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to update allocation');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update allocation');
    }
  }
);

export const deleteEpicAllocation = createAsyncThunk(
  'epics/deleteEpicAllocation',
  async (allocationId: string, { rejectWithValue }) => {
    try {
      const response = await epicsApi.deleteAllocation(allocationId);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to delete allocation');
      }
      return allocationId;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to delete allocation');
    }
  }
);

// Helper to recompute allocatedTeamWeeks on the affected epic after allocation mutations
function recomputeEpicAllocatedTWs(state: EpicsState, epicId: string) {
  const total = state.allocations
    .filter(a => a.epicId === epicId || (a as any).epic?.id === epicId)
    .reduce((sum, a) => sum + Number(a.allocatedTeamWeeks), 0);
  const idx = state.epics.findIndex(e => e.id === epicId);
  if (idx !== -1) {
    state.epics[idx] = { ...state.epics[idx], allocatedTeamWeeks: total };
  }
  if (state.currentEpic?.id === epicId) {
    state.currentEpic = { ...state.currentEpic, allocatedTeamWeeks: total };
  }
}

const epicsSlice = createSlice({
  name: 'epics',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<EpicsState['filters']>>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentEpic: (state, action: PayloadAction<Epic | null>) => {
      state.currentEpic = action.payload;
    },
    setPagination: (state, action: PayloadAction<Partial<EpicsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setSorting: (state, action: PayloadAction<{ sortBy: EpicsState['sortBy']; sortOrder: EpicsState['sortOrder'] }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    setPriorityRankings: (state, action: PayloadAction<any[]>) => {
      state.priorityRankings = action.payload;
    },
    clearAllocationError: (state) => {
      state.allocationError = null;
    },
    setAllocations: (state, action: PayloadAction<EpicAllocation[]>) => {
      state.allocations = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch EPICs
      .addCase(fetchEpics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEpics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.epics = action.payload.epics || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchEpics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch EPIC by ID
      .addCase(fetchEpicById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEpicById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentEpic = action.payload;
      })
      .addCase(fetchEpicById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create EPIC
      .addCase(createEpic.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createEpic.fulfilled, (state, action) => {
        state.isCreating = false;
        state.epics.unshift(action.payload);
      })
      .addCase(createEpic.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update EPIC
      .addCase(updateEpic.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateEpic.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.epics.findIndex(epic => epic.id === action.payload.id);
        if (index !== -1) {
          state.epics[index] = action.payload;
        }
        if (state.currentEpic?.id === action.payload.id) {
          state.currentEpic = action.payload;
        }
      })
      .addCase(updateEpic.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Update EPIC status
      .addCase(updateEpicStatus.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateEpicStatus.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.epics.findIndex(epic => epic.id === action.payload.id);
        if (index !== -1) {
          state.epics[index] = action.payload;
        }
        if (state.currentEpic?.id === action.payload.id) {
          state.currentEpic = action.payload;
        }
      })
      .addCase(updateEpicStatus.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Delete EPIC
      .addCase(deleteEpic.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteEpic.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.epics = state.epics.filter(epic => epic.id !== action.payload);
        if (state.currentEpic?.id === action.payload) {
          state.currentEpic = null;
        }
      })
      .addCase(deleteEpic.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      // Update EPIC priority
      .addCase(updateEpicPriority.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateEpicPriority.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.epics.findIndex(epic => epic.id === action.payload.id);
        if (index !== -1) {
          state.epics[index] = action.payload;
        }
        if (state.currentEpic?.id === action.payload.id) {
          state.currentEpic = action.payload;
        }
      })
      .addCase(updateEpicPriority.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Fetch priority rankings
      .addCase(fetchPriorityRankings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPriorityRankings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.priorityRankings = action.payload.rankings || action.payload;
      })
      .addCase(fetchPriorityRankings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Auto-assign priorities
      .addCase(autoAssignPriorities.pending, (state) => {
        state.isPrioritizing = true;
        state.error = null;
      })
      .addCase(autoAssignPriorities.fulfilled, (state, action) => {
        state.isPrioritizing = false;
        // Update EPICs with new priorities
        const updates = action.payload.updates || [];
        updates.forEach((update: any) => {
          const index = state.epics.findIndex(epic => epic.id === update.epicId);
          if (index !== -1) {
            state.epics[index].priority = update.newPriority;
          }
        });
      })
      .addCase(autoAssignPriorities.rejected, (state, action) => {
        state.isPrioritizing = false;
        state.error = action.payload as string;
      })
      // Reorder priorities
      .addCase(reorderPriorities.pending, (state) => {
        state.isPrioritizing = true;
        state.error = null;
      })
      .addCase(reorderPriorities.fulfilled, (state, action) => {
        state.isPrioritizing = false;
        // Refresh EPICs list to get updated priorities
      })
      .addCase(reorderPriorities.rejected, (state, action) => {
        state.isPrioritizing = false;
        state.error = action.payload as string;
      })
      // Bulk update priorities
      .addCase(bulkUpdatePriorities.pending, (state) => {
        state.isPrioritizing = true;
        state.error = null;
      })
      .addCase(bulkUpdatePriorities.fulfilled, (state, action) => {
        state.isPrioritizing = false;
        // Refresh EPICs list to get updated priorities
      })
      .addCase(bulkUpdatePriorities.rejected, (state, action) => {
        state.isPrioritizing = false;
        state.error = action.payload as string;
      })
      // Fetch EPIC allocations
      .addCase(fetchEpicAllocations.pending, (state) => {
        state.isManagingAllocations = true;
        state.allocationError = null;
      })
      .addCase(fetchEpicAllocations.fulfilled, (state, action) => {
        state.isManagingAllocations = false;
        state.allocations = action.payload.allocations || action.payload;
      })
      .addCase(fetchEpicAllocations.rejected, (state, action) => {
        state.isManagingAllocations = false;
        state.allocationError = action.payload as string;
      })
      // Create EPIC allocation
      .addCase(createEpicAllocation.pending, (state) => {
        state.isManagingAllocations = true;
        state.allocationError = null;
      })
      .addCase(createEpicAllocation.fulfilled, (state, action) => {
        state.isManagingAllocations = false;
        const newAllocation = action.payload.allocation || action.payload;
        state.allocations.push(newAllocation);
        if (newAllocation.epicId) {
          recomputeEpicAllocatedTWs(state, newAllocation.epicId);
        }
      })
      .addCase(createEpicAllocation.rejected, (state, action) => {
        state.isManagingAllocations = false;
        state.allocationError = action.payload as string;
      })
      // Update EPIC allocation
      .addCase(updateEpicAllocation.pending, (state) => {
        state.isManagingAllocations = true;
        state.allocationError = null;
      })
      .addCase(updateEpicAllocation.fulfilled, (state, action) => {
        state.isManagingAllocations = false;
        const updatedAllocation = action.payload.allocation || action.payload;
        const index = state.allocations.findIndex(alloc => alloc.id === updatedAllocation.id);
        if (index !== -1) {
          state.allocations[index] = updatedAllocation;
        }
        if (updatedAllocation.epicId) {
          recomputeEpicAllocatedTWs(state, updatedAllocation.epicId);
        }
      })
      .addCase(updateEpicAllocation.rejected, (state, action) => {
        state.isManagingAllocations = false;
        state.allocationError = action.payload as string;
      })
      // Delete EPIC allocation
      .addCase(deleteEpicAllocation.pending, (state) => {
        state.isManagingAllocations = true;
        state.allocationError = null;
      })
      .addCase(deleteEpicAllocation.fulfilled, (state, action) => {
        state.isManagingAllocations = false;
        const deleted = state.allocations.find(alloc => alloc.id === action.payload);
        const epicId = deleted?.epicId || (deleted as any)?.epic?.id;
        state.allocations = state.allocations.filter(alloc => alloc.id !== action.payload);
        if (epicId) {
          recomputeEpicAllocatedTWs(state, epicId);
        }
      })
      .addCase(deleteEpicAllocation.rejected, (state, action) => {
        state.isManagingAllocations = false;
        state.allocationError = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  setFilters, 
  clearFilters, 
  setCurrentEpic, 
  setPagination,
  setSorting,
  setPriorityRankings,
  clearAllocationError,
  setAllocations
} = epicsSlice.actions;

export default epicsSlice.reducer;
